import { trackLeadCreatedResponse } from "../../../shared/openaiLeadTracking.mjs";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Head from "next/head";
import Image from "next/image";
import { FaCheckCircle, FaPhoneAlt, FaLinkedin, FaFacebook, FaYoutube } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { FaLocationDot, FaInstagram } from "react-icons/fa6";
import { toast, ToastContainer } from "react-toastify";
import { trackLeadHybrid } from "@/lib/metaLeadTracking";
import LeadOtpVerification from "@/components/common/LeadOtpVerification";

const metadata = {
  title: "Contact NEET Support | We are here for you.",
  description:
    "Get in touch with NEET Support for any queries or assistance. We're here to help you with your NEET preparation and counseling needs.",
  keywords: "contact NEET support, NEET queries, NEET assistance",
  canonical: "https://neetsupport.com/contact",
  robots: "index, follow",
};

const MAP_EMBED_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.493694533716!2d77.37707218832476!3d28.614962122169025!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cefb21af8bf5d%3A0x47cabe358d8d8209!2sNEET%20Support%20%7C%20NEET%20PG%2FUG%20counselling%20%7C%20NEET%20PG%2FUG%202026%20%7C%20seat%20allotment%20%7C%20NEET%20PG%2FUG%20colleges%20%7C%20MD%20MS%20admissions!5e0!3m2!1sen!2sin!4v1775800787586!5m2!1sen!2sin";

export default function ContactOne() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  // Lazy-load map only when user scrolls near it (reduces JS/3P cost during initial render)
  const mapWrapRef = useRef(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);

  const contactURL = process.env.NEXT_PUBLIC_ADMIN_URL;

  const faqData = useMemo(
    () => [
      {
        question: "How to get started with NEET Support?",
        answer:
          "Simply, fill in your details to signup and create an account and you can use these credentials to login to your dashboard where you can explore all the different services and tools available for you. Or, you can also call us at +91 956-046-5351 and we’ll personally guide you through the registration and portal access.",
      },
      {
        question: "Can I upgrade my plan after the NEET counselling has begun?",
        answer:
          "Yes, you can and access additional features whenever you feel fit to do so, but you cannot downgrade your plan.",
      },
      {
        question: "What is the difference between Standard and Premium Plan?",
        answer:
          "In the Standard Plan you get full access to our advanced portal and you can access all the tools, but you will miss out on the Pre-Exam Mentorship services and only the Junior Mentor will be assigned in the Post Exam Mentorship. These services and features are very useful if you wish to appear and crack NEET with maximum confidence. In the Premium Plan, along with the advanced AI Tools you’ll be assigned an Academic Mentor and a Success Coach who will guide you and help you to prepare for your exam and stay on the right path always.",
      },
      {
        question: "How much time does it take to solve a query if I raise a ticket?",
        answer:
          "If you fill out the above given contact form and raise a query ticket, our team will get back to you within 2 hours if it’s during work hours. In case you have any immediate queries, you can use the WhatsApp chat option by clicking on the WhatsApp icon in the bottom right corner of the page.",
      },
    ],
    []
  );

  const [activeFAQ, setActiveFAQ] = useState(null);
  const toggleFAQ = (index) => setActiveFAQ((prev) => (prev === index ? null : index));

  useEffect(() => {
    // IntersectionObserver to lazy-load map when it comes near viewport
    // (big improvement for Performance/TBT)
    const el = mapWrapRef.current;
    if (!el || shouldLoadMap) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setShouldLoadMap(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoadMap]);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setSubject("");
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!isOtpVerified) {
      toast.error("Please verify your phone via WhatsApp OTP before submitting.");
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(`${contactURL}/api/contact-details`, {
        firstName,
        lastName,
        email,
        phone,
        subject,
        message,
      });

      trackLeadCreatedResponse(response);

      // stop syanamte submission
      // try {
      //   await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/contacts/upsert`, {
      //     fullName: `${firstName} ${lastName}`.trim(),
      //     email,
      //     phone,
      //     QuerySubject: subject,
      //     message,
      //     tags: "Queries",
      //   });
      // } catch (err) {
      //   // Keep user success but log for debugging
      //   console.error("Upsert failed:", err);
      // }

      if (response.data?.success) {
        await trackLeadHybrid({
          email,
          phone,
          value: 49,
          currency: "INR",
          eventSourceUrl: "/thank-you",
          dedupeKey: `Queries_${phone}_${email}`,
          customData: {
            lead_source: "Queries",
            query_subject: subject,
          },
        });

        setShowSuccess(true);
        toast.success(response.data?.message || "Contact form submitted successfully.");

        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);

        resetForm();
      } else {
        toast.error(
          response.data?.message || "Failed to send the message. Please try again later."
        );
      }
    } catch (error) {
      console.error("Error submitting the contact form:", error);
      toast.error("There was an error submitting your message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <Head>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta charSet="utf-8" />
        <link rel="canonical" href={metadata.canonical} />
        <meta name="robots" content={metadata.robots} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
            {
              "@context": "https://schema.org",
              "@type": "ContactPage",
              "mainEntity": {
                "@type": "LocalBusiness",
                "name": "NEET Support",
                "url": "https://neetsupport.com",
                "logo": "https://neetsupport.com/favicon/neetsupport-logo.webp",
                "description": "Get in touch with NEET Support for any queries or assistance. We're here to help you with your NEET preparation and counseling needs.",
                "telephone": "+91 95604 65351",
                "email": "info@neetsupport.com",
                "address": [
                  {
                    "@type": "PostalAddress",
                    "name": "Head Office",
                    "streetAddress": "Ground Floor, C-127, C Block, Sector 63",
                    "addressLocality": "Noida",
                    "addressRegion": "Uttar Pradesh",
                    "postalCode": "201301",
                    "addressCountry": "IN"
                  },
                ]
              }
            }
          `,
          }}
        />
      </Head>

      <div className="w-full">
        <div className="flex xl:pt-36 xl:px-36 pb-4 mt-24 xl:mt-0">
          <div className="container w-full mx-auto px-0 sm:px-4">
            <div className="flex flex-col xl:flex-row bg-white rounded-lg shadow-xl overflow-hidden mx-auto">
              {/* Left Panel */}
              <div className="relative w-full xl:max-w-md bg-[#097F78] rounded-lg p-6 sm:p-8 text-white shadow-lg overflow-hidden">
                <div className="relative z-10 text-center sm:text-left pb-[20%]">
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    Contact Information
                  </h1>
                  <p className="mb-6 text-base sm:text-[15px]">
                    Got a query? Let’s chat and solve it!
                  </p>

                  <ul className="space-y-4">
                    <li className="flex items-start sm:items-center space-x-2 sm:space-x-4">
                      <FaPhoneAlt className="text-lg sm:text-xl mt-1 sm:mt-0" aria-hidden="true" />
                      <span className="text-sm leading-snug">
                        +91 95604 65351, +91 99109 89253
                      </span>
                    </li>

                    <li className="flex flex-row items-center space-x-4">
                      <MdEmail className="text-lg sm:text-xl" aria-hidden="true" />
                      <span className="text-sm">info@neetsupport.com</span>
                    </li>

                    {/* <li className="flex items-start space-x-4">
                      <FaLocatzionDot className="text-lg sm:text-xl mt-1" aria-hidden="true" />
                      <div className="text-sm text-left leading-6">
                        <p className="font-semibold">Address 1:</p>
                        <p>
                          4th Floor, 415, NEET Support, Plot No. 21 & 21A, Sector 142,
                          Noida, Uttar Pradesh 201304
                        </p>
                      </div>
                    </li> */}

                    <li className="flex items-start space-x-4">
                      <FaLocationDot className="text-lg sm:text-xl mt-1" aria-hidden="true" />
                      <div className="text-sm text-left leading-6">
                        <p className="font-semibold">Address:</p>
                        <p>
                          Ground Floor, C-127, C Block, Sector 63, Noida, Uttar Pradesh 201301
                        </p>
                      </div>
                    </li>
                  </ul>

                  {/* Lazy Map */}
                  <div
                    ref={mapWrapRef}
                    className="mt-8 overflow-hidden rounded-[10px] shadow-md"
                    style={{ height: 260 }}
                  >
                    {!shouldLoadMap ? (
                      <button
                        type="button"
                        onClick={() => setShouldLoadMap(true)}
                        className="w-full h-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition"
                        aria-label="Load Google Map"
                      >
                        <span className="text-white font-semibold">
                          Load map
                        </span>
                      </button>
                    ) : (
                      <iframe
                        title="NEET Support location on Google Maps"
                        src={MAP_EMBED_SRC}
                        style={{ width: "100%", height: "100%", border: 0 }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                      />
                    )}
                  </div>
                </div>

                {/* Decorative corner */}
                <div className="absolute flex bottom-0 right-0" aria-hidden="true">
                  <div className="relative w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 lg:w-28 lg:h-28">
                    <div className="absolute w-[90%] h-[90%] bg-[#84885E] rounded-full bottom-4 right-4 sm:bottom-6 sm:right-6" />
                    <div className="absolute w-full h-full bg-[#FF9245] rounded-tl-full" />
                    <div className="absolute w-10 h-10 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-white rounded-full flex items-center justify-center bottom-[25%] right-[70%]">
                      <Image
                        src="/assets/contact-1/logo.png"
                        alt="NEET Support logo"
                        width={64}
                        height={64}
                        priority={false}
                      />
                    </div>
                  </div>
                </div>

                {/* Social Links (fixed URLs + icons + rel) */}
                <div className="absolute bottom-0 left-0 flex justify-center sm:justify-start space-x-4 mb-4 sm:mb-6 ml-4 sm:ml-6 z-10">
                  <a
                    href="https://www.linkedin.com/"
                    className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#FF9245] text-white rounded-full hover:!bg-white hover:!text-[#FF9245] transition duration-300"
                    aria-label="LinkedIn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaLinkedin className="text-lg sm:text-xl" aria-hidden="true" />
                  </a>

                  <a
                    href="https://www.instagram.com/neetsupportindia/"
                    className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#FF9245] text-white rounded-full hover:!bg-white hover:!text-[#FF9245] transition duration-300"
                    aria-label="Instagram"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaInstagram className="text-lg sm:text-xl" aria-hidden="true" />
                  </a>

                  <a
                    href="https://www.facebook.com/NEETSupport.comIND"
                    className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#FF9245] text-white rounded-full hover:!bg-white hover:!text-[#FF9245] transition duration-300"
                    aria-label="Facebook"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaFacebook className="text-lg sm:text-xl" aria-hidden="true" />
                  </a>

                  <a
                    href="https://www.youtube.com/@NEETSupportIndia"
                    className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#FF9245] text-white rounded-full hover:!bg-white hover:!text-[#FF9245] transition duration-300"
                    aria-label="YouTube"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaYoutube className="text-lg sm:text-xl" aria-hidden="true" />
                  </a>
                </div>
              </div>

              {/* Form Panel */}
              <div className="flex-1 mt-20 ml-6 mr-6 sm:ml-12 sm:mr-12 bg-white">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block mb-2 text-sm font-semibold text-gray-700">
                        First Name
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        className="w-full border-b-2 border-t-0 border-l-0 border-r-0 rounded-none border-gray-300 focus:border-[#FF9245] focus:outline-none p-2 text-gray-700"
                        required
                        placeholder="Enter your first name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                      />
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block mb-2 text-sm font-semibold text-gray-700">
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        className="w-full border-b-2 border-t-0 border-l-0 border-r-0 rounded-none border-gray-300 focus:border-[#FF9245] focus:outline-none p-2 text-gray-700"
                        required
                        placeholder="Enter your last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block mb-2 text-sm font-semibold text-gray-700">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        className="w-full border-b-2 border-t-0 border-l-0 border-r-0 rounded-none border-gray-300 focus:border-[#FF9245] focus:outline-none p-2 text-gray-700"
                        required
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block mb-2 text-sm font-semibold text-gray-700">
                        Phone Number
                      </label>
                      <LeadOtpVerification
                        phone={phone}
                        otpEnabled={false}
                        onPhoneChange={setPhone}
                        inlinePhoneField
                        countryCodePrefix="+91"
                        phoneInputName="phone"
                        phonePlaceholder="Enter 10-digit phone number"
                        phoneInputClassName="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none border-gray-300 focus:border-[#FF9245] text-gray-700"
                        onVerifiedChange={setIsOtpVerified}
                      />
                    </div>
                  </div>

                  <fieldset>
                    <legend className="block mb-2 text-sm font-semibold text-gray-700">
                      Select Subject
                    </legend>
                    <div className="flex flex-wrap items-center gap-6">
                      {["Billing Inquiry", "General Inquiry", "Grievances Inquiry"].map((subj) => {
                        const value = subj; // keep exact label (avoid mismatch with backend)
                        return (
                          <label
                            className="flex items-center text-gray-700 text-sm font-medium"
                            key={value}
                          >
                            <input
                              type="radio"
                              name="subject"
                              value={value}
                              checked={subject === value}
                              onChange={(e) => setSubject(e.target.value)}
                              required
                              className="form-radio text-[#FF9245] focus:ring-[#FF9245]"
                            />
                            <span className="ml-2">{subj}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>

                  <div>
                    <label htmlFor="message" className="block mb-2 text-sm font-semibold text-gray-700">
                      Message
                    </label>
                    <textarea
                      id="message"
                      className="w-full border-b-2 border-gray-300 focus:border-[#FF9245] focus:outline-none p-2 text-gray-700"
                      rows={4}
                      required
                      placeholder="Write your message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </div>

                  <div className="relative mt-8 flex flex-col items-center">
                    <button
                      type="submit"
                      className="self-end bg-[#FF9245] hover:bg-[#e67d3a] text-xl text-white py-2 px-6 rounded-xl font-semibold shadow-md transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Send Message"}
                    </button>

                    <div className="self-end mt-4">
                      <Image
                        src="/assets/contact-1/letter_send 2.png"
                        alt="Send illustration"
                        width={180}
                        height={180}
                        className="animate-bounce"
                        priority={false}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* FAQ */}
            <section className="w-full max-w-7xl mx-auto mt-12 px-4">
              <h2 className="sm:text-3xl text-2xl font-bold text-center capitalize">
                Frequently asked <span className="text-[#ff9245]">questions</span>
              </h2>

              <div className="mt-8 space-y-4 ml-2 mr-2">
                {faqData.map((faq, index) => {
                  const open = activeFAQ === index;
                  return (
                    <div
                      key={index}
                      className={`p-4 border rounded-lg shadow-md ${open ? "bg-gray-50" : ""}`}
                    >
                      <button
                        type="button"
                        className="w-full flex justify-between items-center text-left cursor-pointer"
                        onClick={() => toggleFAQ(index)}
                        aria-expanded={open}
                        aria-controls={`faq-panel-${index}`}
                      >
                        <h3 className="text-lg font-semibold">{faq.question}</h3>
                        <span aria-hidden="true">{open ? "-" : "+"}</span>
                      </button>

                      <div
                        id={`faq-panel-${index}`}
                        className={`mt-2 text-gray-700 text-left text-base ${open ? "block" : "hidden"}`}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </div>

        {/* Success Popup */}
        {showSuccess && (
          <div
            className="fixed inset-0 bg-gray-900 bg-opacity-60 flex justify-center items-center z-50"
            role="dialog"
            aria-modal="true"
            aria-label="Submission successful"
          >
            <div className="bg-white rounded-lg p-6 text-center w-[90%] md:w-[400px] shadow-lg">
              <FaCheckCircle className="text-green-500 text-7xl mx-auto mb-4" aria-hidden="true" />
              <h3 className="text-2xl font-semibold text-gray-700">Thank you!</h3>
              <p className="mt-2 text-gray-600">Your submission has been sent successfully.</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
